"use client";

import {useContext, useEffect, useState} from "react";
import {ApprovalLookupBudgetContext} from "./ApprovalLookupBudget";

type LookupReader = {required: boolean};
type LookupEntry<T> = {promise: Promise<T>; readers: Set<LookupReader>};

/** Fetchers mark network, timeout, throttling, and server failures as worth retrying. */
export class ApprovalLookupError extends Error {
   constructor(readonly transient: boolean) {
      super("Reference name unavailable");
   }
}

// Attempts that start with a required reader share this limit; optional reads bypass it.
const REQUIRED_CONCURRENCY = 6;
const REQUIRED_RETRY_DELAYS_MS = [500, 2000];
const waiting: (() => Promise<void>)[] = [];
let running = 0;

function drainRequired() {
   while (running < REQUIRED_CONCURRENCY && waiting.length) {
      const job = waiting.shift()!;
      running++;
      void job().finally(() => {
         running--;
         drainRequired();
      });
   }
}

function queueRequired<T>(start: () => Promise<T>, wanted: () => boolean) {
   return new Promise<T>((resolve, reject) => {
      waiting.push(async () => {
         // Readers that unmounted while queued must not still cost a request.
         if (!wanted()) return reject(new ApprovalLookupError(false));
         try {
            resolve(await start());
         } catch (error) {
            reject(error);
         }
      });
      drainRequired();
   });
}

/** Any mounted required reader grants retries to the shared request, regardless of mount order. */
async function lookup<T>(start: () => Promise<T>, readers: ReadonlySet<LookupReader>) {
   const wanted = () => readers.size > 0;
   const required = () => {
      for (const reader of readers) {
         if (reader.required) return true;
      }
      return false;
   };
   for (let attempt = 0; ; attempt++) {
      if (!wanted()) throw new ApprovalLookupError(false);
      try {
         return await (required() ? queueRequired(start, wanted) : start());
      } catch (error) {
         if (attempt >= REQUIRED_RETRY_DELAYS_MS.length || !(error instanceof ApprovalLookupError && error.transient) || !required()) throw error;
         // Wait outside the queue so a retry never holds a slot other targets could use.
         await new Promise(resolve => setTimeout(resolve, REQUIRED_RETRY_DELAYS_MS[attempt]));
         if (!required()) throw error;
      }
   }
}

/**
 * Share reference data only while reviews use it; keys include organization and API version.
 * Optional reads spend the review's budget; required reads are queued and retried instead.
 */
export function createSharedApprovalLookup<T>(fetcher: (key: string) => Promise<T>) {
   const entries = new Map<string, LookupEntry<T>>();
   return function useSharedApprovalLookup(key: string | null, required = false): T | undefined {
      const reserveLookup = useContext(ApprovalLookupBudgetContext);
      const [result, setResult] = useState<{key: string; value: T} | null>(null);
      useEffect(() => {
         if (!key) return;
         let active = true;
         const reader = {required};
         let entry = entries.get(key);
         if (!entry) {
            if (!required && reserveLookup && !reserveLookup()) return;
            const readers = new Set([reader]);
            entry = {readers, promise: lookup(() => fetcher(key), readers)};
            entries.set(key, entry);
         } else {
            entry.readers.add(reader);
         }
         const current = entry;
         void current.promise.then(value => {
            if (active) setResult({key, value});
         }).catch(() => {
            if (active) setResult(null);
            // A later reader can retry a failed request.
            if (entries.get(key) === current) entries.delete(key);
         });
         return () => {
            active = false;
            current.readers.delete(reader);
            // Preserve Strict Mode's immediate remount without keeping stale data after navigation.
            queueMicrotask(() => {
               if (!current.readers.size && entries.get(key) === current) entries.delete(key);
            });
         };
      }, [key, required, reserveLookup]);
      return key && result?.key === key ? result.value : undefined;
   };
}
